import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  InsertDriveFile as InsertDriveFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import api from '@/services/api'

interface Document {
  id: string
  type: string
  title?: string
  createdOn: string
  status?: 'pending' | 'authorized' | 'rejected'
  downloadUrl?: string
}

const IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
]

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const isImageFile = (file: File): boolean => IMAGE_TYPES.includes(file.type)

interface DocumentsSectionProps {
  contractId: string // MongoDB contract ID
  documents?: Document[] // Optional: if provided, will use these instead of fetching
  loading?: boolean // Optional: if provided, will use this instead of internal loading
  onUpload?: () => void
  canUpload?: boolean // Optional: gate the Upload button (defaults to true)
}

interface ApiDocument {
  _id: string
  contract_id: string
  title: string
  type: string
  fileUrl: string
  fileKey: string
  notes_id?: string
  patientId?: string
  therapistId?: string
  isDeleted: boolean
  createdAt: string
  updatedAt: string
  __v?: number
}

interface DocumentsApiResponse {
  totalRecords: number
  totalPages: number
  currentPage: string
  limit: string
  data: ApiDocument[]
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  contractId,
  documents: externalDocuments,
  loading: externalLoading,
  onUpload,
  canUpload = true,
}) => {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadFilePreview, setUploadFilePreview] = useState<string | null>(
    null
  )
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  const fetchDocuments = useCallback(async () => {
    if (!contractId) return

    setLoading(true)
    setError(null)
    try {
      const response = await api.get<DocumentsApiResponse>(
        `/contracts/${contractId}/documents-session`,
        {
          params: {
            page,
            limit,
          },
        }
      )

      const responseData = response.data

      const mappedDocuments: Document[] = (responseData.data || []).map(
        (doc: ApiDocument) => ({
          id: doc._id,
          type: doc.type,
          title: doc.title,
          createdOn: doc.createdAt,
          downloadUrl: doc.fileUrl,
        })
      )

      setDocuments(mappedDocuments)
      setTotalPages(responseData.totalPages || 1)
      setTotal(responseData.totalRecords || 0)
    } catch (err: any) {
      console.error('Failed to fetch documents:', err)
      setError(
        err.response?.data?.message ||
          'Failed to load documents. Please try again.'
      )
      setDocuments([])
    } finally {
      setLoading(false)
    }
  }, [contractId, page, limit])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true)
    setUploadTitle('')
    setUploadFile(null)
    setUploadFilePreview(null)
    setUploadError(null)
    setIsDragOver(false)
  }

  const handleCloseUploadDialog = () => {
    if (!uploadLoading) {
      if (uploadFilePreview) {
        URL.revokeObjectURL(uploadFilePreview)
      }
      setUploadDialogOpen(false)
      setUploadTitle('')
      setUploadFile(null)
      setUploadFilePreview(null)
      setUploadError(null)
      setIsDragOver(false)
    }
  }

  const handleFileSelect = (file: File | null) => {
    if (uploadFilePreview) {
      URL.revokeObjectURL(uploadFilePreview)
      setUploadFilePreview(null)
    }
    if (file && isImageFile(file)) {
      setUploadFilePreview(URL.createObjectURL(file))
    }
    setUploadFile(file)
    setUploadError(null)
  }

  const handleRemoveFile = () => {
    handleFileSelect(null)
  }

  const handleUploadSubmit = async () => {
    if (!uploadTitle.trim()) {
      setUploadError('Please enter a document title')
      return
    }
    if (!uploadFile) {
      setUploadError('Please select a file to upload')
      return
    }

    setUploadLoading(true)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('file', uploadFile)
      formData.append('title', uploadTitle.trim())
      formData.append('contractId', contractId)

      const response = await api.post('/contracts/upload-session-doc', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      await fetchDocuments()
      onUpload?.()
      handleCloseUploadDialog()

      const successMessage = response?.data?.message || 'Document uploaded successfully'
      setSnackbarMessage(successMessage)
      setSnackbarOpen(true)
    } catch (err: any) {
      console.error('Failed to upload document:', err)
      setUploadError(
        err.response?.data?.message ||
          'Failed to upload document. Please try again.'
      )
    } finally {
      setUploadLoading(false)
    }
  }

  // Use external loading state if provided, otherwise use internal
  const isLoading = externalLoading !== undefined ? externalLoading : loading

  const handleDownload = async (doc: Document) => {
    if (downloadingId || !doc.downloadUrl) return

    setDownloadingId(doc.id)
    try {
      // Fetch the file as a blob to force download instead of opening in new tab
      const response = await fetch(doc.downloadUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = doc.title || `document-${doc.id}.pdf`
      link.style.display = 'none'

      // Append to body, click, and remove
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
      }, 100)
    } catch (error) {
      console.error('Failed to download document:', error)
      // Fallback: open in new tab if download fails
      if (doc.downloadUrl) {
        window.open(doc.downloadUrl, '_blank')
      }
    } finally {
      setDownloadingId(null)
    }
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'authorized':
        return 'success'
      case 'pending':
        return 'warning'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'authorized':
        return 'Authorized'
      case 'pending':
        return 'Authorization Pending'
      case 'rejected':
        return 'Rejected'
      default:
        return ''
    }
  }

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }

  return (
    <>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Documents</Typography>
            {contractId && canUpload && (
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenUploadDialog}
                size="small"
              >
                Upload
              </Button>
            )}
          </Box>

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          ) : documents.length === 0 ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography variant="body2" color="text.secondary">
                No documents available
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, color: 'primary.main' }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: 'primary.main' }}
                      >
                        Created On
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: 'primary.main' }}
                      >
                        Download
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map(doc => (
                      <TableRow key={doc.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {doc.title || doc.type}
                            </Typography>
                            {doc.status && (
                              <Chip
                                label={getStatusLabel(doc.status)}
                                color={getStatusColor(doc.status)}
                                size="small"
                                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(doc.createdOn), 'MM/dd/yyyy')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc)}
                            disabled={downloadingId === doc.id}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            {downloadingId === doc.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {total > 0 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={2}
                  pt={2}
                  borderTop="1px solid"
                  borderColor="divider"
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {documents.length} of {total} documents
                  </Typography>
                  {totalPages > 1 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        startIcon={<ChevronLeftIcon />}
                        onClick={handlePreviousPage}
                        disabled={page === 1 || isLoading}
                        variant="outlined"
                      >
                        Previous
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        Page {page} of {totalPages}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ChevronRightIcon />}
                        onClick={handleNextPage}
                        disabled={page >= totalPages || isLoading}
                        variant="outlined"
                      >
                        Next
                      </Button>
                    </Stack>
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.16)',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.06)',
          },
        }}
      >
        <Box
          sx={{
            background: 'primary.main',
            backgroundColor: 'primary.main',
            color: 'white',
            px: 3,
            py: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <DialogTitle
                sx={{
                  color: 'inherit',
                  fontWeight: 700,
                  fontSize: '1.35rem',
                  p: 0,
                  lineHeight: 1.3,
                }}
              >
                Upload Document
              </DialogTitle>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.25 }}
              >
                Add a new document to this contract
              </Typography>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3, pt: 3 }}>
          {uploadError &&
            !uploadError.includes('title') &&
            !uploadError.includes('file') && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                  borderRadius: 2,
                  border: '1px solid rgba(211, 47, 47, 0.2)',
                }}
              >
                <Typography variant="body2" color="error">
                  {uploadError}
                </Typography>
              </Box>
            )}

          <TextField
            fullWidth
            label="Document Title"
            placeholder="e.g. Session notes, Treatment plan..."
            value={uploadTitle}
            onChange={e => {
              setUploadTitle(e.target.value)
              if (uploadError) setUploadError(null)
            }}
            error={!!uploadError && uploadError.includes('title')}
            helperText={
              uploadError?.includes('title') ? uploadError : undefined
            }
            autoFocus
            sx={{
              mb: 2.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'grey.50',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: 'grey.100' },
                '&.Mui-focused': { bgcolor: 'white' },
              },
            }}
          />

          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}
          >
            File
          </Typography>
          <input
            accept="*/*"
            style={{ display: 'none' }}
            id="document-file-upload"
            type="file"
            onChange={e => {
              handleFileSelect(e.target.files?.[0] || null)
              e.target.value = ''
            }}
          />

          {uploadFile ? (
            <Box
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'grey.50',
              }}
            >
              {uploadFilePreview ? (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={uploadFilePreview}
                    alt={uploadFile.name}
                    sx={{
                      width: '100%',
                      height: 220,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveFile}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: 'white',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <ImageIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(uploadFile.size)}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() =>
                        document.getElementById('document-file-upload')?.click()
                      }
                      sx={{ ml: 0.5 }}
                    >
                      Replace
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor:
                        uploadFile.type === 'application/pdf'
                          ? '#ef4444'
                          : 'primary.light',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {uploadFile.type === 'application/pdf' ? (
                      <PictureAsPdfIcon sx={{ fontSize: 32 }} />
                    ) : (
                      <InsertDriveFileIcon sx={{ fontSize: 32 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(uploadFile.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleRemoveFile}
                    size="small"
                    color="error"
                  >
                    <CloseIcon />
                  </IconButton>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      document.getElementById('document-file-upload')?.click()
                    }
                  >
                    Replace
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <label htmlFor="document-file-upload">
              <Box
                onDragOver={e => {
                  e.preventDefault()
                  setIsDragOver(true)
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={e => {
                  e.preventDefault()
                  setIsDragOver(false)
                  const file = e.dataTransfer.files?.[0]
                  if (file) handleFileSelect(file)
                }}
                sx={{
                  py: 4,
                  px: 3,
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: isDragOver ? 'primary.main' : 'grey.300',
                  bgcolor: isDragOver ? 'rgba(13, 148, 136, 0.08)' : 'grey.50',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: isDragOver
                      ? 'rgba(13, 148, 136, 0.08)'
                      : 'grey.100',
                  },
                }}
              >
                <CloudUploadIcon
                  sx={{
                    fontSize: 56,
                    color: isDragOver ? 'primary.main' : 'grey.400',
                    mb: 1.5,
                    transition: 'color 0.2s',
                  }}
                />
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color="text.primary"
                >
                  {isDragOver
                    ? 'Drop file here'
                    : 'Drag and drop or click to browse'}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  PDF, images, or any document type
                </Typography>
              </Box>
            </label>
          )}

          {uploadError?.includes('file') && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: 'block' }}
            >
              {uploadError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            gap: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Button
            onClick={handleCloseUploadDialog}
            disabled={uploadLoading}
            sx={{ fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadSubmit}
            disabled={uploadLoading || !uploadTitle.trim() || !uploadFile}
            startIcon={
              uploadLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            sx={{
              px: 2.5,
              py: 1,
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
            }}
          >
            {uploadLoading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default DocumentsSection
